import Carousel from '@components/carousel/dynamicCarousel';
import { map, mergeMap, of } from 'rxjs';
import onArticleCardCarouselSettingsReady$ from './articleCardCarouselSettings';
import getArticleSection$ from '../apis/getArticleSection';

const arrowsHtml = `
  <button aria-label="Previous" class="carousel-prev" data-left-arrow>
    <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd"
        d="M7.647 13.02a.5.5 0 0 0 .707-.707L4.54 8.5h8.126a.5.5 0 1 0 0-1H4.54l3.813-3.813a.5.5 0 0 0-.707-.707L2.984 7.642a.503.503 0 0 0 0 .715l4.663 4.663Z"
        fill="#222" />
    </svg>
  </button>
  <button aria-label="Next" class="carousel-next" data-right-arrow>
    <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd"
        d="M8.353 13.02a.5.5 0 1 1-.707-.707L11.46 8.5H3.332a.5.5 0 0 1 0-1h8.127L7.646 3.687a.5.5 0 0 1 .707-.707l4.663 4.662a.498.498 0 0 1 0 .715L8.354 13.02Z"
        fill="#222" />
    </svg>
  </button>
`;

/**
 * Observable that emits the HTML content for an article card carousel once the settings are ready.
 * 
 * This observable listens for the `onArticleCardCarouselSettingsReady$` event, processes the settings,
 * and retrieves the article search results if a search command is provided. If no search command is
 * specified, it emits an empty string for the article cards HTML.
 * 
 * @constant
 * @type {Observable<{ articleCardsHtml: string, carouselQuerySelector: string }>}
 * 
 * @emits {Object} - An object containing:
 *   - `articleCardsHtml` {string}: The HTML content for the article cards.
 *   - `carouselQuerySelector` {string}: The query selector for the carousel element.
 */
const onArticleCardCarouselReady$ = onArticleCardCarouselSettingsReady$.pipe(
  mergeMap((settings) => {
    const articleSectionCommand = settings.articleSectionCommand;
    const carouselQuerySelector = settings.carouselQuerySelector;
    const shouldAddArrows = settings.shouldAddArrows;

    // If no search command is provided, emit an empty string
    if (!articleSectionCommand) {
      return of({ carouselQuerySelector, shouldAddArrows });
    }

    return getArticleSection$(articleSectionCommand).pipe(
      map((articleSection) => ({ articleSection, carouselQuerySelector, shouldAddArrows }))
    );
  })
);

/**
 * Replaces the carousel element specified by the selector with new HTML content.
 *
 * @param {string} carouselSelector - The CSS selector for the carousel element to replace.
 * @param {string} html - The HTML string to insert in place of the carousel element.
 */
const replaceCarouselContent = (carouselSelector, html) => {
  const carouselElement = document.querySelector(carouselSelector);
  if (!carouselElement) {
    return;
  }

  const fragment = document.createRange().createContextualFragment(html);
  carouselElement.replaceWith(fragment);
};

/**
 * Appends arrow controls to a carousel element by injecting the provided HTML.
 *
 * @param {string} carouselSelector - The CSS selector for the carousel element.
 * @param {string} html - The HTML string representing the arrow controls to append.
 */
const appendCarouselArrows = (carouselSelector, html) => {
  const carouselElement = document.querySelector(carouselSelector);
  if (!carouselElement) {
    return;
  }

  const fragment = document.createRange().createContextualFragment(html);
  carouselElement.appendChild(fragment);
};

const setArticleCardCarousel = function (carouselQuerySelector) {
  const carouselSettings = {
    itemListQuerySelector: `${carouselQuerySelector} [data-item-list]`,
    previousQuerySelector: `${carouselQuerySelector} [data-left-arrow]`,
    nextQuerySelector: `${carouselQuerySelector} [data-right-arrow]`,
  }
  const carousel = new Carousel();
  carousel.setSettings(carouselSettings);
  carousel.create();
};

onArticleCardCarouselReady$.subscribe(({articleSection, carouselQuerySelector, shouldAddArrows}) => {
  if (articleSection) {
    replaceCarouselContent(carouselQuerySelector, articleSection);
  } else if (shouldAddArrows) {
    appendCarouselArrows(carouselQuerySelector, arrowsHtml);
  }
  setArticleCardCarousel(carouselQuerySelector);
});

export default onArticleCardCarouselSettingsReady$;